import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createApiClient } from '../api';
import { useSession } from '../session';
import { config } from '../../config';
import { ensureTicket, heldTicket, subscribeTickets, type HeldTicket } from './tickets';
import { useFairnessEnabled } from './useFairnessEnabled';

// A slider swipe across packs must not burn the per-session ticket budget:
// a pack asks for its ticket only once it has stayed on screen this long.
const SETTLE_MS = 600;
// No network unless the held ticket is near expiry — this only re-checks.
const CHECK_EVERY_MS = 5_000;

/**
 * Keeps a fresh ticket for the pack on screen, so the player can see the
 * hashed server seed before buying and the purchase never waits for one.
 * Idle while provably fair is off, the player has no session yet, or the
 * page is in the background.
 */
export function useFairnessTicket(offerId: string | null): HeldTicket | null {
  const enabled = useFairnessEnabled();
  const session = useSession();
  const hasToken = session.token !== null;
  const client = useMemo(
    () => createApiClient({ baseUrl: config.apiUrl, getToken: session.getToken }),
    [session.getToken],
  );
  const active = enabled && hasToken && !!offerId;

  useEffect(() => {
    if (!active || !offerId) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') void ensureTicket(client, offerId);
    };
    const settle = setTimeout(refresh, SETTLE_MS);
    const check = setInterval(refresh, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearTimeout(settle);
      clearInterval(check);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [active, client, offerId]);

  const ticket = useSyncExternalStore(
    subscribeTickets,
    () => (offerId ? heldTicket(offerId) : null),
    () => null,
  );
  return active ? ticket : null;
}
